import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { AMRDetail, BaseURL, PageURLMaster } from "../APIs/APIUrls";
import { GetAllPageBannerforWeb } from "../APIs/APIHelperforWeb";

const allowedStaticRoutes = [
  "home",
  "contact-us",
  "privacy-policy",
  "about-us",
  "our-services",
  "blog-grid",
  "blog-detail",
  "author",
  "404",
  "dashboard",
  "add-property",
  "add-photos",
  "add-amenities",
  "add-floor-plan",
  "add-document",
  "add-near-amenities",
  "my-profile",
  "buy-subscription",
  "manage-enquiry",
];

const normalizePath = (value = "") => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "/";

  let pathValue = rawValue;
  try {
    if (/^https?:\/\//i.test(rawValue)) {
      pathValue = new URL(rawValue).pathname;
    }
  } catch (error) {
    pathValue = rawValue;
  }

  const withoutQueryOrHash = decodeURIComponent(pathValue)
    .split(/[?#]/)[0]
    .trim()
    .toLowerCase();

  if (!withoutQueryOrHash) return "/";

  const withLeadingSlash = withoutQueryOrHash.startsWith("/")
    ? withoutQueryOrHash
    : `/${withoutQueryOrHash}`;

  return withLeadingSlash.length > 1
    ? withLeadingSlash.replace(/\/+/g, "/").replace(/\/+$/, "")
    : withLeadingSlash;
};

const resolveImageUrl = (imagePath = "") => {
  const value = String(imagePath || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) {
    return value;
  }
  const normalizedPath = value.startsWith("/") ? value : `/${value}`;
  return `${BaseURL.replace(/\/+$/, "")}${normalizedPath}`;
};

const getBestBannerMatch = (rows, activePath) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const scoredMatches = rows
    .map((item) => {
      const bannerPath = normalizePath(item?.PageURL);
      if (!bannerPath || bannerPath === "/") return null;

      let score = 0;
      if (bannerPath === activePath) {
        score = 4;
      } else if (activePath.startsWith(`${bannerPath}/`)) {
        score = 3;
      } else if (bannerPath.startsWith(`${activePath}/`)) {
        score = 2;
      } else {
        const activeSlug = activePath.split("/").filter(Boolean).pop();
        const bannerSlug = bannerPath.split("/").filter(Boolean).pop();
        if (activeSlug && bannerSlug && activeSlug === bannerSlug) {
          score = 1;
        }
      }

      if (!score) return null;
      return { item, score, pathLength: bannerPath.length };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score || b.pathLength - a.pathLength);

  return scoredMatches[0]?.item || null;
};

const getUniqueCities = (rows) => {
  if (!Array.isArray(rows)) return [];

  const uniqueCities = new Map();
  rows.forEach((row) => {
    const rawCity = row?.City || row?._objDetailPage?.City;
    if (!rawCity) return;
    const trimmedCity = String(rawCity).trim();
    const normalizedCity = trimmedCity.toLowerCase();
    if (normalizedCity && !uniqueCities.has(normalizedCity)) {
      uniqueCities.set(normalizedCity, trimmedCity);
    }
  });

  return Array.from(uniqueCities.values());
};

const parseLatLongFromEmbededMapURL = (embededMapURL) => {
  if (!embededMapURL || typeof embededMapURL !== "string") {
    return { latitude: "", longitude: "" };
  }

  const googleMatch = embededMapURL.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (googleMatch) {
    return {
      latitude: googleMatch[1],
      longitude: googleMatch[2],
    };
  }

  const llMatch = embededMapURL.match(/[?&](?:ll|q)=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (llMatch) {
    return {
      latitude: llMatch[1],
      longitude: llMatch[2],
    };
  }

  const atMatch = embededMapURL.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?),/);
  if (atMatch) {
    return {
      latitude: atMatch[1],
      longitude: atMatch[2],
    };
  }

  return { latitude: "", longitude: "" };
};

const LocalBusiness = () => {
  const { pathname } = useLocation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schemaImage, setSchemaImage] = useState("https://askmerealty.in/office-image.jpg");
  const [pageUrlMasterData, setPageUrlMasterData] = useState([]);
  const [areaServed, setAreaServed] = useState(["Rohtak", "Delhi", "Gurugram", "Noida", "Faridabad", "Ghaziabad"]);

  const pathSegments = pathname.split("/").filter(Boolean);
  const isPropertyTitleRoute = pathSegments.length === 1 && !allowedStaticRoutes.includes(pathSegments[0]);
  const isPropertyDetailsRoute = pathSegments.length === 2 && pathSegments[0] === "property-details";
  const shouldRenderSchema = isPropertyTitleRoute || isPropertyDetailsRoute;

  useEffect(() => {
    const loadBannerImage = async () => {
      if (!shouldRenderSchema) return;

      try {
        const rows = await GetAllPageBannerforWeb();
        const activePath = normalizePath(pathname);
        const matchedBanner = getBestBannerMatch(rows, activePath);
        const imageUrl = matchedBanner?.Image ? resolveImageUrl(matchedBanner.Image) : "https://askmerealty.in/office-image.jpg";
        setSchemaImage(imageUrl);
      } catch (error) {
        console.error("Error loading LocalBusiness banner image:", error);
      }
    };

    loadBannerImage();
  }, [pathname, shouldRenderSchema]);

  useEffect(() => {
    const loadPageUrlMasterData = async () => {
      if (!shouldRenderSchema) return;

      try {
        const cached = localStorage.getItem("PageURLMasterListforWeb");
        let rows = Array.isArray(cached ? JSON.parse(cached) : null)
          ? JSON.parse(cached)
          : [];

        if (!rows.length) {
          const response = await fetch(`${PageURLMaster}GetAllPageURLMasterforWeb`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({}),
          });

          if (response.ok) {
            const result = await response.json();
            rows = result?.data || result?.Data || [];
          }
        }

        if (Array.isArray(rows)) {
          setPageUrlMasterData(rows);
          if (!cached) {
            localStorage.setItem("PageURLMasterListforWeb", JSON.stringify(rows));
          }
        }
      } catch (error) {
        console.error("Error loading LocalBusiness page URL master data:", error);
      }
    };

    loadPageUrlMasterData();
  }, [shouldRenderSchema]);

  useEffect(() => {
    const uniqueCities = getUniqueCities(pageUrlMasterData);
    if (uniqueCities.length) {
      setAreaServed(uniqueCities);
    }
  }, [pageUrlMasterData]);

  useEffect(() => {
    if (!shouldRenderSchema) {
      setLoading(false);
      return;
    }

    const fetchAMRDetail = async () => {
      try {
        const cached = localStorage.getItem("localBusinessSchemaData");
        const cachedTs = Number(localStorage.getItem("localBusinessSchemaData_Timestamp"));
        const isCacheValid = !Number.isNaN(cachedTs) && Date.now() - cachedTs < 5 * 60 * 1000;

        if (cached && isCacheValid) {
          setDetail(JSON.parse(cached));
          setLoading(false);
          return;
        }

        const response = await fetch(`${AMRDetail}GetAMRDetailByIdforweb/1`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed with status ${response.status}`);
        }

        const result = await response.json();
        const dataSource = result?.data ?? result;
        const item = Array.isArray(dataSource) ? dataSource[0] : dataSource;

        if (item) {
          setDetail(item);
          localStorage.setItem("localBusinessSchemaData", JSON.stringify(item));
          localStorage.setItem("localBusinessSchemaData_Timestamp", String(Date.now()));
        }
      } catch (error) {
        console.error("Error fetching LocalBusiness AMR detail:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAMRDetail();
  }, [shouldRenderSchema]);

  if (!shouldRenderSchema || pathname === "/404" || loading || !detail) {
    return null;
  }

  const detailData = detail || {};
  const { latitude: parsedLatitude, longitude: parsedLongitude } = parseLatLongFromEmbededMapURL(detailData.EmbededMapURL);
  const latitude = detailData.Latitude || parsedLatitude || "28.8955";
  const longitude = detailData.Longitude || parsedLongitude || "76.6066";

  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: detailData.Value || "AskMe Realty",
    image: schemaImage,
    telephone: detailData.ContactNo || "+91-XXXXXXXXXX",
    email: detailData.Email || "info@askmerealty.in",
    address: {
      "@type": "PostalAddress",
      streetAddress: detailData.Address || "Sector 14, Near City Center",
      addressLocality: "Rohtak",
      addressRegion: "Haryana",
      postalCode: "124001",
      addressCountry: "IN",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude,
      longitude,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ],
        opens: "09:00",
        closes: "19:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Sunday",
        opens: "10:00",
        closes: "17:00",
      },
    ],
    priceRange: "₹₹",
    paymentAccepted: ["Cash", "Credit Card", "Bank Transfer", "UPI"],
    areaServed: areaServed.length
      ? areaServed
      : ["Rohtak", "Delhi", "Gurugram", "Noida", "Faridabad", "Ghaziabad"],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default LocalBusiness;
